import React, { useCallback, useEffect, useState } from 'react';
import {
  useManifest,
  useManifests,
  useManifestsByIds,
  useSearchIndex,
} from '../../services/Firestore/Resources';
import { FairDate } from '../../services/Utils/Date';
import { DataTable } from '../../components/DataTable/DataTable';
import { sortByKey } from '../../services/Utils/sorting';
import { useDebounce } from '../../services/Utils/useDebounce';
import Input from '../../components/Input/Input';
import { RenderState } from '../../components/State/State';
import { createUseStyles } from 'react-jss';
import { ManifestHoverMenu } from '../../components/ManifestList/ManifestHoverMenu';

const useStyles = createUseStyles({
  menuComponent: {
    color: 'white',
    position: 'absolute',
    width: '75px',
    backgroundColor: 'WindowFrame',
    border: '5px',
    boxSizing: 'border-box',
    padding: '5px',
    textAlign: 'center',
    lineHeight: '1.2',
    borderRadius: '5px',
    zIndex: 15,
    cursor: 'auto',
  },
});

const ManifestList = ({
  onRowClick,
  onStateChange,
  className,
  defaultQuery = '',
  defaultLimit = 25,
  defaultOrder = [['date', 'desc']],
  defaultStartAtHistory = [],
  ...props
}) => {
  const classes = useStyles();
  const [points, setPoints] = useState({
    x: 0,
    y: 0,
  });
  const [ids, setIds] = useState([]);
  const [selectedManifestId, setSelectedManifestId] = useState(null);
  const [manifests, setManifests] = useState([]);
  const [startAtHistory, setStartAtHistory] = useState(defaultStartAtHistory);
  const [order, setOrder] = useState(defaultOrder);
  const [limit, setLimit] = useState(defaultLimit);
  const [query, setQuery] = useState(defaultQuery);
  const [startAtDoc, startAtDocLoading] = useManifest(startAtHistory[0]);
  const [loading, setLoading] = useState(false);
  const [queriedManifests, queriedManifestsLoading] = useManifests({
    limit,
    orderBy: order,
    startAfter: startAtDoc?._snapshot,
  });
  const [searchedManifests, searchedManifestsLoading] = useManifestsByIds(...ids);
  const [searchResults, searchLoading] = useSearchIndex(query, 'manifests', 100);
  const [searchDebounce, searchPending] = useDebounce();
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    return () => {
      setIds([]);
      setManifests([]);
      setQuery(defaultQuery);
      setStartAtHistory(defaultStartAtHistory);
      setOrder(defaultOrder);
    };
  }, []);

  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        startAtHistory,
        order,
        limit,
        query,
      });
    }
  }, [startAtHistory, order, limit, query, onStateChange]);

  useEffect(() => {
    if (searchResults !== null) {
      setIds(searchResults.map((sr) => sr.id));
    }
  }, [searchResults]);

  useEffect(() => {
    setLoading(
      queriedManifestsLoading ||
        searchedManifestsLoading ||
        searchLoading ||
        searchPending ||
        startAtDocLoading,
    );
  }, [
    queriedManifestsLoading,
    searchedManifestsLoading,
    searchLoading,
    searchPending,
    startAtDocLoading,
  ]);

  useEffect(() => {
    setManifests((manifests) => {
      if (searchPending) {
        return manifests;
      }

      if (query.length === 0) {
        return queriedManifests;
      }
      return sortByKey(searchedManifests, order);
    });
  }, [query, queriedManifests, searchedManifests, order, searchPending]);

  const handleSearch = useCallback(
    (query) => {
      searchDebounce(() => {
        setIds((prevIds) => (query.length === 0 ? [] : prevIds));
        setStartAtHistory([]);
        setQuery(query);
      });
    },
    [searchDebounce, setIds, setQuery, setStartAtHistory],
  );

  const handlePagination = useCallback(
    ({ page }) => {
      if (page < 0) {
        setStartAtHistory((data) => {
          const newData = [...data];
          newData.shift();
          return newData;
        });
      } else {
        setStartAtHistory((data) => {
          const newData = [...data];
          newData.unshift(manifests[manifests.length - 1].id);
          return newData;
        });
      }
    },
    [setStartAtHistory, manifests],
  );

  const handleSort = useCallback(
    (key, dir) => {
      setOrder((order) => {
        let newOrder = [];
        if (dir !== 'none') {
          newOrder.push([key, dir]);
        }
        return newOrder;
      });
      setStartAtHistory([]);
    },
    [setOrder, setStartAtHistory],
  );

  const handleLimitChange = useCallback(
    (e) => {
      setLimit(e.limit);
    },
    [setLimit],
  );

  const handleContextMenu = (event, row) => {
    event.preventDefault();
    setSelectedManifestId(row.id);
    setPoints({
      x: event.pageX,
      y: event.pageY,
    });
    setShowMenu(true);
  };
  const handleStopClick = (event) => {
    // Stops clicking in the menu from propagating to the parent div
    event.stopPropagation();
  };
  const handleLeaveMenu = () => {
    setShowMenu(false);
  };

  const menuComponent = (id) => {
    return <ManifestHoverMenu id={id} />;
  };

  const handleRowClick = useCallback(
    (event, row) => {
      if (onRowClick) {
        onRowClick(event, row.id);
      }
    },
    [onRowClick],
  );

  const columns = [
    {
      key: 'date',
      render: (value, row) => FairDate(value.toDate()),
      sortable: true,
      minWidth: true,
    },
    {
      key: 'manifestNumber',
      label: 'DO-MF NO',
      sortable: true,
      minWidth: true,
    },
    {
      key: 'name',
      sortable: true,
    },
    {
      key: 'project',
      sortable: true,
    },
    {
      key: 'reference',
      sortable: true,
    },
    {
      key: 'senderName',
      label: 'Sender',
      sortable: true,
    },
    {
      key: 'recipientName',
      label: 'Recipient',
      sortable: true,
    },
    {
      key: 'state',
      label: 'Status',
      render: (v, row) => RenderState(v, row.stateUpdated),
      sortable: true,
      minWidth: true,
    },
  ];

  return (
    <>
      <div
        className={className}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <div style={{ display: 'flex' }}>
          <Input
            style={{ minWidth: 540 }}
            minLength={3}
            placeholder={'Search'}
            defaultValue={query}
            onKeyUp={(e) => {
              const value = e.target.value;
              if (value.length >= 3 || value.length === 0) {
                handleSearch(e.target.value);
              }
            }}
          ></Input>
          {query.length > 0 && !searchLoading && <p>Found {searchResults?.length} manifests.</p>}
        </div>
        {showMenu && (
          <div
            onClick={handleStopClick}
            onMouseLeave={handleLeaveMenu}
            className={classes.menuComponent}
            style={{
              top: points.y,
              left: points.x,
            }}
          >
            {menuComponent(selectedManifestId)}
          </div>
        )}
        {manifests.length > 0 && (
          <DataTable
            style={{ flex: 1 }}
            data={manifests}
            order={order}
            columns={columns}
            loading={loading}
            paginatePrevDisabled={startAtHistory.length === 0 || loading}
            paginateNextDisabled={loading || manifests.length < limit || !!query}
            onRowClick={handleRowClick}
            onRowContextMenu={handleContextMenu}
            limitDisabled={!!query}
            showProgress={true}
            progress={
              searchPending ? undefined : searchLoading ? 33 : searchedManifestsLoading ? 66 : 100
            }
            onPaginate={handlePagination}
            onSort={handleSort}
            onLimitChange={handleLimitChange}
            defaultLimit={limit}
            {...props}
          />
        )}
      </div>
    </>
  );
};
export default ManifestList;
